import React, { useEffect, useRef, useState, useCallback } from 'react';
import { BrowserQRCodeReader } from '@zxing/browser';
import { NotFoundException } from '@zxing/library';
import api from '../utils/api';

export default function QRScanner({ onResult, onCancel }) {
  const videoRef     = useRef(null);
  const controlsRef  = useRef(null); // controles de ZXing para detener limpiamente
  const [error,      setError]     = useState('');
  const [scanning,   setScanning]  = useState(false);
  const [manualCode, setManualCode]= useState('');
  const [buscando,   setBuscando]  = useState(false);

  const detener = useCallback(() => {
    if (controlsRef.current) {
      try { controlsRef.current.stop(); } catch (_) {}
      controlsRef.current = null;
    }
  }, []);

  const iniciarEscaner = useCallback(() => {
    if (!videoRef.current) return;
    const reader = new BrowserQRCodeReader();
    reader.decodeFromVideoDevice(undefined, videoRef.current, (result, err) => {
      if (result) {
        const valor = result.getText().trim();
        detener();
        setScanning(false);
        fetchAscensor(valor);
      }
      if (err && !(err instanceof NotFoundException)) {
        console.warn('ZXing:', err?.message);
      }
    })
      .then(controls => {
        controlsRef.current = controls;
        setScanning(true);
      })
      .catch(() => {
        setError('No se pudo acceder a la cámara. Usa el código manual.');
      });
  }, [detener]);

  useEffect(() => {
    iniciarEscaner();
    return () => detener(); // limpieza al desmontar (flecha atrás, navegación)
  }, [iniciarEscaner, detener]);

  const fetchAscensor = async (valor) => {
    setBuscando(true);
    try {
      const res = await api.get(`/ascensores/qr/${encodeURIComponent(valor)}`);
      onResult(res.data);
    } catch {
      setError(`QR no reconocido. Valor leído: "${valor}"`);
      setBuscando(false);
      iniciarEscaner();
    }
  };

  const handleManual = async (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    setBuscando(true);
    try {
      const res = await api.get(`/ascensores/codigo/${manualCode.trim().toUpperCase()}`);
      detener();
      onResult(res.data);
    } catch {
      setError('Código no encontrado. Verifica e intenta de nuevo.');
      setBuscando(false);
    }
  };

  if (buscando) return (
    <div style={{ textAlign: 'center', padding: 40 }}>
      <div className="spinner" />
      <p style={{ color: '#777', marginTop: 12 }}>Buscando ascensor...</p>
    </div>
  );

  return (
    <div className="qr-container">
      <p style={{ fontSize: 14, color: '#555', marginBottom: 16 }}>
        Apunta la cámara al código QR del ascensor
      </p>

      <div style={{
        position: 'relative', width: 240, height: 240, margin: '0 auto 20px',
        borderRadius: 16, overflow: 'hidden',
        border: scanning ? '3px solid #0f6e56' : '3px dashed #ccc',
      }}>
        <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          muted playsInline autoPlay />
        {scanning && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
          }}>
            <div style={{
              width: 160, height: 160, border: '2px solid rgba(15,110,86,0.8)',
              borderRadius: 12, boxShadow: '0 0 0 9999px rgba(0,0,0,0.25)',
            }} />
          </div>
        )}
        {!scanning && !error && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            background: '#e8f5f1', color: '#0f6e56', gap: 8,
          }}>
            <span style={{ fontSize: 40 }}>📷</span>
            <span style={{ fontSize: 13 }}>Iniciando cámara...</span>
          </div>
        )}
      </div>

      {error && (
        <p style={{ color: '#c62828', fontSize: 13, marginBottom: 12 }}>{error}</p>
      )}

      <div style={{ margin: '16px 0 8px', color: '#aaa', fontSize: 13 }}>
        — o ingresa el código manualmente —
      </div>

      <form onSubmit={handleManual} style={{ display: 'flex', gap: 8 }}>
        <input
          value={manualCode}
          onChange={e => setManualCode(e.target.value)}
          placeholder="Ej: ABA01 o ABANICOS 1A"
          style={{
            flex: 1, padding: '10px 14px', border: '1.5px solid #ddd',
            borderRadius: 10, fontSize: 15, textTransform: 'uppercase',
            fontFamily: 'inherit',
          }}
        />
        <button type="submit" className="btn btn-primary"
          style={{ width: 'auto', padding: '10px 18px' }}>
          Buscar
        </button>
      </form>

      <button className="btn btn-secondary" style={{ marginTop: 16 }}
        onClick={() => { detener(); onCancel(); }}>
        Cancelar
      </button>
    </div>
  );
}
